import { useState, useEffect, useCallback, useRef } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import {
  Plus, X, RefreshCw, GripVertical,
  Globe2, Inbox, Calendar, ListTodo,
  Wallet, TrendingUp, Settings as SettingsIcon,
  ChevronsLeftRight, ChevronsRightLeft, ChevronsUpDown, ChevronsDownUp,
  Users, BarChart3, Sparkles,
} from "lucide-react";

import TrafficWidget from "./widgets/TrafficWidget";
import LeadsWidget from "./widgets/LeadsWidget";
import AppointmentsSummaryWidget from "./widgets/AppointmentsSummaryWidget";
import ActionItemsWidget from "./widgets/ActionItemsWidget";
import ClientsKpiWidget from "./widgets/ClientsKpiWidget";
import FinanceKpiWidget from "./widgets/FinanceKpiWidget";
import ChartsWidget from "./widgets/ChartsWidget";
import DemoPortalWidget from "./widgets/DemoPortalWidget";

const MAX_WIDGETS = 10;
const MAX_W = 2;
const MAX_H = 3;

const WIDGET_CATALOG = {
  // KPIs métier (lecture seule)
  clients_kpi:           { label: "Clients",              desc: "Total, nouveaux, dossiers, corbeille", Icon: Users,        tone: "indigo",  Comp: ClientsKpiWidget },
  finance_kpi:           { label: "Finance",              desc: "Factures payées / en attente / retard", Icon: Wallet,       tone: "emerald", Comp: FinanceKpiWidget },
  // Widgets opérationnels existants
  traffic:               { label: "Trafic site",          desc: "Visites, leads, top sources",    Icon: Globe2,       tone: "cyan",    Comp: TrafficWidget },
  leads:                 { label: "Leads récents",        desc: "Formulaires reçus",              Icon: Inbox,        tone: "emerald", Comp: LeadsWidget },
  appointments_summary:  { label: "Rendez-vous",          desc: "Synthèse RDV + propositions",    Icon: Calendar,     tone: "violet",  Comp: AppointmentsSummaryWidget },
  action_items:          { label: "Actions urgentes",     desc: "Factures + tâches en retard",    Icon: ListTodo,     tone: "red",     Comp: ActionItemsWidget },
  charts:                { label: "Graphiques",           desc: "Revenus/dépenses + répartition", Icon: BarChart3,    tone: "blue",    Comp: ChartsWidget },
  // Batch #14.232 — Widget Portail démo (visites + prospects avec infos complètes)
  demo_portal:           { label: "Portail démo",         desc: "Visites + prospects du démo",    Icon: Sparkles,     tone: "violet",  Comp: DemoPortalWidget },
  kpi:                   { label: "KPI",                  desc: "Bientôt disponible",             Icon: TrendingUp,   tone: "blue",    Comp: null, disabled: true },
};

const TONE_HEADER = {
  cyan:    "bg-cyan-50 text-cyan-700",
  emerald: "bg-emerald-50 text-emerald-700",
  amber:   "bg-amber-50 text-amber-700",
  rose:    "bg-rose-50 text-rose-700",
  violet:  "bg-violet-50 text-violet-700",
  red:     "bg-red-50 text-red-700",
  blue:    "bg-blue-50 text-blue-700",
  indigo:  "bg-indigo-50 text-indigo-700",
};

// Tailwind classes explicites pour que le purge ne les supprime pas
const SPAN_CLASSES = {
  "1-1": "lg:col-span-1 lg:row-span-1",
  "1-2": "lg:col-span-1 lg:row-span-2",
  "1-3": "lg:col-span-1 lg:row-span-3",
  "2-1": "lg:col-span-2 lg:row-span-1",
  "2-2": "lg:col-span-2 lg:row-span-2",
  "2-3": "lg:col-span-2 lg:row-span-3",
};

export default function ConfigurableDashboard() {
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [resizingId, setResizingId] = useState(null);
  // Map id → optimistic size pendant l'attente du PUT
  const optimisticSize = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/dashboard/widgets");
      setWidgets(r.data?.widgets || []);
      optimisticSize.current = {};
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (type) => {
    if (widgets.length >= MAX_WIDGETS) {
      toast.error("Maximum 10 widgets. Retirez-en un.", { id: "max-widgets" });
      return;
    }
    try {
      await api.post("/dashboard/widgets", { type });
      setShowAddModal(false);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail || "Erreur";
      if (detail === "max_widgets_reached") {
        toast.error("Maximum 10 widgets. Retirez-en un.", { id: "max-widgets" });
      } else {
        toast.error(detail);
      }
    }
  };

  const handleDelete = async (id, e) => {
    e?.stopPropagation();
    try {
      await api.delete(`/dashboard/widgets/${id}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;

    const reordered = Array.from(widgets);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setWidgets(reordered);
    try {
      await api.put("/dashboard/widgets/reorder", {
        widget_ids: reordered.map((w) => w.id),
      });
    } catch {
      toast.error("Erreur de réorganisation");
      load();
    }
  };

  const tryOpenAdd = () => {
    if (widgets.length >= MAX_WIDGETS) {
      toast.error("Maximum 10 widgets. Retirez-en un.", { id: "max-widgets" });
      return;
    }
    setShowAddModal(true);
  };

  const handleResize = async (widget, dim, delta) => {
    const currSize = widget.size || { w: 1, h: 1 };
    const next = { ...currSize, [dim]: Math.max(1, Math.min(dim === "w" ? MAX_W : MAX_H, currSize[dim] + delta)) };
    if (next.w === currSize.w && next.h === currSize.h) return;
    // Optimistic update
    setWidgets((prev) => prev.map((w) => (w.id === widget.id ? { ...w, size: next } : w)));
    setResizingId(widget.id);
    try {
      await api.put(`/dashboard/widgets/${widget.id}/size`, next);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de redimensionnement");
      load();
    } finally {
      setResizingId(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-2" data-testid="configurable-dashboard">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
            <SettingsIcon className="w-5 h-5 text-blue-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Tableau de bord</h1>
            <p className="text-sm text-gray-500">{widgets.length}/{MAX_WIDGETS} widgets · cliquez sur les flèches en haut à droite pour redimensionner</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className="p-2 rounded-lg hover:bg-gray-100 transition"
            aria-label="Rafraîchir"
            data-testid="dashboard-cfg-refresh"
            title="Rafraîchir"
          >
            <RefreshCw className={`w-4 h-4 text-gray-500 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button
            onClick={tryOpenAdd}
            className="px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition text-sm font-medium flex items-center gap-1.5"
            data-testid="add-widget-btn"
          >
            <Plus className="w-4 h-4" />
            Ajouter
          </button>
        </div>
      </div>

      {!loading && widgets.length === 0 && (
        <div className="min-h-[60vh]" data-testid="dashboard-cfg-empty" />
      )}

      {widgets.length > 0 && (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="dashboard-widgets" direction="vertical">
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="grid grid-cols-1 lg:grid-cols-2 lg:auto-rows-[200px] gap-4"
                data-testid="dashboard-widgets-grid"
              >
                {widgets.map((w, idx) => {
                  const meta = WIDGET_CATALOG[w.type];
                  if (!meta) return null;
                  const { Icon, label, tone, Comp } = meta;
                  const size = w.size || { w: 1, h: 1 };
                  const spanClass = SPAN_CLASSES[`${size.w}-${size.h}`] || SPAN_CLASSES["1-1"];
                  const isResizing = resizingId === w.id;
                  return (
                    <Draggable key={w.id} draggableId={w.id} index={idx}>
                      {(prov, snap) => (
                        <div
                          ref={prov.innerRef}
                          {...prov.draggableProps}
                          className={`bg-white rounded-xl border border-gray-200 overflow-hidden animate-card-in select-none ${spanClass} flex flex-col ${
                            snap.isDragging ? "shadow-2xl ring-2 ring-blue-300 rotate-1" : "hover:shadow-md"
                          } ${isResizing ? "ring-2 ring-blue-200" : ""} transition-all duration-200`}
                          style={{
                            ...prov.draggableProps.style,
                            animationDelay: `${idx * 60}ms`,
                          }}
                          data-testid={`widget-card-${w.type}`}
                        >
                          {/* Header */}
                          <div className={`px-3 py-2 flex items-center justify-between ${TONE_HEADER[tone]} flex-shrink-0`}>
                            {/* Drag handle */}
                            <div
                              {...prov.dragHandleProps}
                              className="flex items-center gap-2 flex-1 cursor-grab active:cursor-grabbing min-w-0"
                              data-testid={`widget-drag-handle-${w.id}`}
                            >
                              <GripVertical className="w-3.5 h-3.5 opacity-60 flex-shrink-0" />
                              <Icon className="w-4 h-4 flex-shrink-0" />
                              <span className="font-semibold text-sm truncate">{label}</span>
                            </div>

                            {/* Resize controls */}
                            <div className="flex items-center gap-0.5 ml-2" onMouseDown={(e) => e.stopPropagation()}>
                              <ResizeBtn
                                disabled={size.w <= 1}
                                onClick={() => handleResize(w, "w", -1)}
                                title="Réduire la largeur"
                                Icon={ChevronsRightLeft}
                                testId={`widget-shrink-w-${w.id}`}
                              />
                              <ResizeBtn
                                disabled={size.w >= MAX_W}
                                onClick={() => handleResize(w, "w", 1)}
                                title="Élargir"
                                Icon={ChevronsLeftRight}
                                testId={`widget-grow-w-${w.id}`}
                              />
                              <span className="w-px h-3.5 bg-current opacity-30 mx-1" />
                              <ResizeBtn
                                disabled={size.h <= 1}
                                onClick={() => handleResize(w, "h", -1)}
                                title="Réduire la hauteur"
                                Icon={ChevronsDownUp}
                                testId={`widget-shrink-h-${w.id}`}
                              />
                              <ResizeBtn
                                disabled={size.h >= MAX_H}
                                onClick={() => handleResize(w, "h", 1)}
                                title="Augmenter la hauteur"
                                Icon={ChevronsUpDown}
                                testId={`widget-grow-h-${w.id}`}
                              />
                              <span className="w-px h-3.5 bg-current opacity-30 mx-1" />
                              <span className="text-[10px] font-mono opacity-60 px-1" data-testid={`widget-size-label-${w.id}`}>
                                {size.w}×{size.h}
                              </span>
                              <button
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => handleDelete(w.id, e)}
                                className="p-1 rounded hover:bg-white/60 transition ml-1"
                                aria-label="Retirer"
                                data-testid={`widget-remove-${w.id}`}
                                title="Retirer le widget"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>

                          {/* Body — overflow gérable selon la taille */}
                          <div className="p-4 flex-1 overflow-auto">
                            {Comp ? <Comp /> : null}
                          </div>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      {showAddModal && (
        <AddWidgetModal
          available={Object.keys(WIDGET_CATALOG).filter(
            (t) => !widgets.find((w) => w.type === t) && !WIDGET_CATALOG[t].disabled
          )}
          onAdd={handleAdd}
          onClose={() => setShowAddModal(false)}
        />
      )}
    </div>
  );
}

function ResizeBtn({ disabled, onClick, title, Icon, testId }) {
  return (
    <button
      onMouseDown={(e) => e.stopPropagation()}
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testId}
      className="p-1 rounded hover:bg-white/70 transition disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
    >
      <Icon className="w-3.5 h-3.5" />
    </button>
  );
}

function AddWidgetModal({ available, onAdd, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 animate-fade-in"
      data-testid="add-widget-modal"
    >
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-gray-900">Ajouter un widget</h3>
            <button onClick={onClose} className="p-1 rounded hover:bg-gray-100" aria-label="Fermer">
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>

          {available.length === 0 ? (
            <p className="text-center text-gray-400 py-8">Tous les types sont déjà ajoutés.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {available.map((type) => {
                const { Icon, label, desc, tone } = WIDGET_CATALOG[type];
                return (
                  <button
                    key={type}
                    onClick={() => onAdd(type)}
                    className="text-left p-4 rounded-lg border border-gray-200 hover:border-blue-400 hover:bg-blue-50/30 transition flex items-start gap-3 group"
                    data-testid={`add-widget-option-${type}`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${TONE_HEADER[tone]} group-hover:scale-110 transition`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-gray-900">{label}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{desc}</div>
                    </div>
                    <Plus className="w-4 h-4 text-gray-400 group-hover:text-blue-600 mt-0.5" />
                  </button>
                );
              })}
            </div>
          )}

          <button
            onClick={onClose}
            className="w-full mt-5 px-4 py-2.5 border border-gray-200 rounded-lg text-sm hover:bg-gray-50 transition"
            data-testid="add-widget-cancel-btn"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}
